import { useMemo, useState } from 'react';
import { Button, Spinner } from '@parsina/framework';
import { useTranslation } from '../../i18n';
import { C } from '../../constants/theme';
import { usePatientPolicies, useInsuranceQuote, useReferralLetters, useInsurerName } from '../../hooks/useInsurance';
import ReferralLetterDialog from './ReferralLetterDialog';
import { SOURCE } from '../../lib/insurance';

/**
 * «سهم بیمار» before the patient sits down — and, when an insurer is waiting on
 * a معرفی‌نامه, the chance to register it here rather than discover it at the
 * cash desk.
 *
 * ONE panel for booking and for check-in, because the question is the same at
 * both: what will this visit cost, and is anything stopping an insurer from
 * paying its part. Two copies would be two ideas of what a quote says.
 *
 * Every insurer gets a line, including the ones paying nothing: «طرف قرارداد
 * نیست» and «معرفی‌نامه لازم است» are precisely what the desk must act on, and
 * they are invisible in the total.
 *
 * A quote is ADVISORY — its failure never blocks a booking, so the panel simply
 * does not appear.
 */

const CARD = {
  border: `1px solid ${C.border}`, borderRadius: 10, background: C.card,
  padding: '9px 11px', display: 'flex', flexDirection: 'column', gap: 6,
};

const LINE = { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem' };

export default function InsuranceQuotePanel({
  patientId,
  doctorId,
  services = [],
  // «خدمت نوبت» — the PRIMARY service the booking is about to store. Quoted by
  // the same resolver that prices the proforma, so the preview is the whole
  // bill rather than only the extras beside it.
  serviceDeclarationId = null,
  atTs,
  appointmentId,
  choice,
  onChoiceChange,
  enabled = true,
}) {
  const { t, moneyUnit, formatMoney } = useTranslation();
  const nameOf = useInsurerName();
  const { supplementaries } = usePatientPolicies(patientId, { activeOnly: true });
  const { letters, create } = useReferralLetters(patientId, {
    enabled: !!patientId && enabled,
  });
  const [dialogFor, setDialogFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const { quote, loading } = useInsuranceQuote({
    patientId, doctorId, services, atTs, appointmentId, choice, enabled,
    serviceDeclarationId,
  });

  // Which policy each waiting insurer belongs to, so «ثبت» opens the dialog on
  // the right one rather than asking a question already answered.
  const policyByInsurer = useMemo(
    () => new Map(supplementaries.map((p) => [p.insurerId, p.id])),
    [supplementaries],
  );

  const excluded = choice?.excludedPolicyIds || [];
  const toggleExcluded = (policyId) => {
    if (!onChoiceChange) return;
    onChoiceChange({
      ...(choice || {}),
      excludedPolicyIds: excluded.includes(policyId)
        ? excluded.filter((id) => id !== policyId)
        : [...excluded, policyId],
    });
  };

  const onSaveLetter = async (input) => {
    setSaving(true);
    try {
      const letter = await create(input);
      // Bind it to THIS visit straight away: the desk registered it because this
      // quote asked for it, and making them pick it again would be a second
      // question with one possible answer.
      if (letter && onChoiceChange) {
        const pairs = (choice?.letterByPolicyId || [])
          .filter((p) => p.policyId !== input.policyId);
        onChoiceChange({
          ...(choice || {}),
          letterByPolicyId: [...pairs, { policyId: input.policyId, letterId: letter.id }],
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!enabled || !patientId) return null;
  if (loading && !quote) return <Spinner data-testid="insurance-quote-loading" />;
  if (!quote) return null;

  // Every insurer named across the quote's lines, each with the worst thing it
  // has to say — a leg that needs a letter on ANY line needs one.
  const insurerRows = [];
  for (const line of quote.lines || []) {
    for (const leg of line.legs || []) {
      let row = insurerRows.find((r) => r.insurerId === leg.insurerId);
      if (!row) {
        row = {
          insurerId: leg.insurerId,
          name: nameOf(leg.insurerId, leg.insurerName),
          share: 0,
          kind: leg.kind,
          contracted: !!leg.contractId,
          needsLetter: false,
        };
        insurerRows.push(row);
      }
      row.share += leg.shareRial || 0;
      if (leg.source === SOURCE.NEEDS_REFERRAL) row.needsLetter = true;
      if (leg.contractId) row.contracted = true;
    }
  }

  const verdicts = (quote.lines || [])
    .map((line) => line.configVerdictFa)
    .filter((v, i, all) => v && all.indexOf(v) === i);

  return (
    <div style={CARD} data-testid="insurance-quote-panel">
      <div style={{ ...LINE, fontWeight: 700 }}>
        <span>{t('insurance.share.patient')}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="insurance-quote-patient">
          {`${formatMoney(quote.patientShareRial, { currency: quote?.currency })} ${moneyUnit('short', quote?.currency)}`}
        </span>
      </div>

      {insurerRows.map((row) => {
        const policyId = policyByInsurer.get(row.insurerId);
        const isExcluded = policyId && excluded.includes(policyId);
        return (
          <div style={LINE} key={row.insurerId} data-testid={`insurance-quote-leg-${row.insurerId}`}>
            <span style={{ color: isExcluded ? C.textMuted : C.textPrimary }}>{row.name}</span>
            <span style={{ flex: 1 }} />
            {isExcluded ? (
              <span style={{ color: C.textMuted, fontSize: '0.71875rem' }}>
                {t('insurance.legs.excludeThisVisit')}
              </span>
            ) : row.needsLetter ? (
              <>
                <span style={{ color: C.amber, fontSize: '0.71875rem', fontWeight: 600 }}>
                  {t('insurance.explain.needsReferral')}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDialogFor(policyId || '')}
                  data-testid={`insurance-quote-letter-${row.insurerId}`}
                >
                  {t('insurance.letter.add')}
                </Button>
              </>
            ) : !row.contracted ? (
              <span style={{ color: C.textMuted, fontSize: '0.71875rem' }}>
                {t('insurance.explain.notContracted')}
              </span>
            ) : (
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {`${formatMoney(row.share, { currency: row?.currency })} ${moneyUnit('short', row?.currency)}`}
              </span>
            )}
            {/* Skipping one تکمیلی for THIS visit — a per-visit choice that lands
                on the invoice's legs and never on the patient's policy. */}
            {policyId && onChoiceChange ? (
              <button
                type="button"
                onClick={() => toggleExcluded(policyId)}
                aria-label={isExcluded
                  ? t('insurance.legs.includeThisVisit')
                  : t('insurance.legs.excludeThisVisit')}
                title={isExcluded
                  ? t('insurance.legs.includeThisVisit')
                  : t('insurance.legs.excludeThisVisit')}
                data-testid={`insurance-quote-exclude-${row.insurerId}`}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                  color: C.textMuted, fontSize: '0.8125rem', lineHeight: 1,
                }}
              >
                {isExcluded ? '↺' : '×'}
              </button>
            ) : null}
          </div>
        );
      })}

      {verdicts.map((verdict) => (
        <div key={verdict} style={{ fontSize: '0.6875rem', color: C.textMuted, lineHeight: 1.8 }}>
          {verdict}
        </div>
      ))}

      <ReferralLetterDialog
        open={dialogFor !== null}
        onClose={() => setDialogFor(null)}
        onSave={onSaveLetter}
        policies={supplementaries}
        policyId={dialogFor || null}
        appointmentId={appointmentId}
        saving={saving}
      />
    </div>
  );
}
